import { css } from '@emotion/react';
import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { getLabelPaddingsValues } from '~/_shared/components/labelVisualizer/labelVisualizer.constants';
import type {
  LabelVisualizerArrowProps, LabelVisualizerBodyProps, LabelVisualizerBorderProps,
} from '~/_shared/components/labelVisualizer/labelVisualizer.types';
import type { QuadrilateralDimensions } from '~/_shared/types/coordinateSystem/coordinateSystem';
import {
  hexWithOpacity, mixColorMemoized,
} from '~/_shared/utils/colors/colors.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';

export const wrapperStyle = css({
  position: 'relative',
});

export const baseLabelStyle = ({ bodyProps, arrowProps, borderProps, maxDimensions }: LabelOptions) => {
  const { top, bottom, left, right } = getLabelPaddingsValues(bodyProps);
  const arrowHeight = arrowProps.dimensions?.height || 0;

  return css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: bodyProps.fontSize,
    left: 0,
    minHeight: bodyProps.dimensions?.height,
    padding: `${top}px ${right}px ${bottom}px ${left}px`,
    top: 0,
    boxSizing: 'border-box',
    borderRadius: borderProps.radius,
    ...(bodyProps.dimensions?.width && { minWidth: bodyProps.dimensions.width }),
    ...(maxDimensions && { maxHeight: maxDimensions.height, maxWidth: maxDimensions.width }),
    marginBottom: arrowHeight,
  });
};

export const labelStyle = ({ bodyProps, arrowProps, borderProps, maxDimensions }: LabelOptions, parentBGColor: string, contrastColor?: string) => {
  const { selectedColor: fillColor, opacity: fillOpacity } = bodyProps.backgroundColor;
  const fillColorOverBackground = mixColorMemoized(parentBGColor, fillColor, fillOpacity ?? 1).toHexString();

  const arrowColors = {
    before: guaranteeHash(borderProps.color.selectedColor),
    after: fillColorOverBackground,
  };

  return css([
    baseLabelStyle({ bodyProps, arrowProps, borderProps, maxDimensions }), {
      backgroundColor: (notNullsy(fillOpacity) && fillOpacity < 1) ? hexWithOpacity(fillColor, fillOpacity) : guaranteeHash(fillColor),
      border: `${borderProps.width}px solid ${guaranteeHash(borderProps.color.selectedColor)}`,
      boxShadow: `0 0 4px 0px ${contrastColor}`,
      color: 'transparent',
      position: 'relative',
      zIndex: 0,
      ...createArrowPseudoElements(arrowProps, borderProps, arrowColors),
    }]);
};

export const textOnlyStyle = ({ bodyProps, arrowProps, borderProps, maxDimensions }: LabelOptions, contrastColor?: string) => {
  // For text-only, both pseudo-elements are transparent.
  const arrowColors = { before: 'transparent', after: 'transparent' };

  return css([
    baseLabelStyle({ bodyProps, arrowProps, borderProps, maxDimensions }), {
      backgroundColor: 'transparent',
      border: `${borderProps.width}px solid transparent`,
      color: guaranteeHash(bodyProps.fontColor.selectedColor),
      opacity: 1,
      position: 'absolute',
      textShadow: `0 0 4px ${contrastColor}`,
      zIndex: 1,
      ...createArrowPseudoElements(arrowProps, borderProps, arrowColors),
    },
  ]);
};

const arrowStyle = ({ borderColor, height, halfWidth, leftIndent, bottomInset }: {
  borderColor: string;
  height: number;
  leftIndent: string;
  halfWidth: number;
  bottomInset: number;
}) => css({
  borderLeft: `${halfWidth}px solid transparent`,
  borderRight: `${halfWidth}px solid transparent`,
  borderTop: `${height}px solid ${guaranteeHash(borderColor)}`,
  content: '""',
  display: 'block',
  height: '0',
  left: leftIndent,
  position: 'absolute',
  bottom: bottomInset - height,
  width: '0',
});

const createArrowProps = (
  arrowProps: LabelVisualizerArrowProps,
  borderProps: LabelVisualizerBorderProps,
  arrowColor: string,
  bottomInset: number
) => {
  const arrowWidth = arrowProps.dimensions?.width || 0;
  const arrowHeight = arrowProps.dimensions?.height || 0;
  const arrowHalfWidth = arrowWidth / 2;
  const arrowLeftDistance = `calc(50% - ${arrowHalfWidth}px)`;

  return arrowStyle({
    leftIndent: `calc(${arrowLeftDistance} - ${borderProps.width}px)`,
    halfWidth: arrowHalfWidth + borderProps.width,
    height: arrowHeight + borderProps.width,
    borderColor: guaranteeHash(arrowColor),
    bottomInset,
  });
};

const createArrowPseudoElements = (
  arrowProps: LabelVisualizerArrowProps,
  borderProps: LabelVisualizerBorderProps,
  arrowColors: { before: string; after: string }
) => {
  return {
    //this is the bigger triangle representing the arrow-border
    '&:before': createArrowProps(
      arrowProps,
      borderProps,
      arrowColors.before,
      0 // border triangle has no inset
    ),
    //this is the smaller triangle representing the arrow fill itself
    '&:after': createArrowProps(
      arrowProps,
      borderProps,
      arrowColors.after,
      Math.sqrt(2) * borderProps.width // fill triangle inset
    ),
  };
};

type LabelOptions = Readonly<{
  bodyProps: LabelVisualizerBodyProps;
  arrowProps: LabelVisualizerArrowProps;
  borderProps: LabelVisualizerBorderProps;
  maxDimensions?: QuadrilateralDimensions;
}>;
